/**
 * Mapbox ベクタタイル (streets-v8) の `road` レイヤーを **HTTP で直接取得**する。
 *
 * 従来はオフスクリーンの WebGL プローブ地図に jumpTo → querySourceFeatures していたが、
 * プローブは WebGL コンテキスト疲弊で「メイン地図は正常なのにタイル0件を返し続ける」
 * 故障モードに入り (実測)、スナップ・進入点列挙が実行によって黙って劣化していた。
 * タイルを HTTP で取得して pbf をデコードすれば、描画非依存で:
 *   - WebGL 故障モードが存在しない
 *   - 視野スイープ不要 = bbox を覆うタイルを**正確に全部**読める (候補数の変動が消える)
 *   - プローブ直列化 (chain) も不要で並列に速い
 *
 * タイルはセッション内キャッシュ (同じ物件の再取得・監査で再利用)。
 * 失敗の意味論: **1枚でも取れなければ null** (部分結果はカバレッジ変動を再導入するため
 * 返さない)。呼び出し側は null でリトライ (キャッシュ済みタイルは再取得しない)。
 */
import { PbfReader } from 'pbf';
import { VectorTile } from '@mapbox/vector-tile';
import { MAPBOX_TOKEN } from '@/services/mapbox';
import { fetchWithTimeout } from '@/lib/net/fetch-timeout';
import {
  padBbox,
  tilesCoveringBbox,
} from '@/lib/geo/tile-math';
import type { TileRoadFeature } from '@/lib/map/bridge-detect';

const TILESET = 'mapbox.mapbox-streets-v8';
/** road レイヤーの取得ズーム。細街路・歩道 (class=path) まで載る最大ズーム。 */
export const ROAD_TILE_ZOOM = 16;
/** 1回の取得で許すタイル数の上限 (bbox の暴走ガード。z16 で ~24 枚 ≈ 3km四方)。 */
const MAX_TILES_PER_FETCH = 24;
/** タイル1枚の取得タイムアウト (ms)。 */
const TILE_TIMEOUT_MS = 10_000;
/** セッション内タイルキャッシュの上限 (超えたら古い順に捨てる)。 */
const TILE_CACHE_MAX = 200;

const tileCache = new Map<string, Promise<TileRoadFeature[]>>();

function cacheSet(key: string, value: Promise<TileRoadFeature[]>) {
  if (tileCache.size >= TILE_CACHE_MAX) {
    const oldest = tileCache.keys().next().value;
    if (oldest !== undefined) tileCache.delete(oldest);
  }
  tileCache.set(key, value);
}

async function fetchTile(
  z: number,
  x: number,
  y: number,
): Promise<TileRoadFeature[]> {
  const url =
    `https://api.mapbox.com/v4/${TILESET}/${z}/${x}/${y}.vector.pbf` +
    `?access_token=${MAPBOX_TOKEN}`;
  const res = await fetchWithTimeout(url, TILE_TIMEOUT_MS);
  // 海上などデータの無いタイルは 404 = 「道なし」(正常)。
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`tile ${z}/${x}/${y}: HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const vt = new VectorTile(new PbfReader(new Uint8Array(buf)));
  const layer = vt.layers['road'];
  if (!layer) return [];
  const out: TileRoadFeature[] = [];
  for (let i = 0; i < layer.length; i++) {
    const geo = layer.feature(i).toGeoJSON(x, y, z);
    out.push({
      properties: geo.properties as TileRoadFeature['properties'],
      geometry: geo.geometry as TileRoadFeature['geometry'],
    });
  }
  return out;
}

function tileFeatures(
  z: number,
  x: number,
  y: number,
): Promise<TileRoadFeature[]> {
  const key = `${z}/${x}/${y}`;
  const hit = tileCache.get(key);
  if (hit) return hit;
  const p = fetchTile(z, x, y);
  cacheSet(key, p);
  // 失敗はキャッシュに残さない (次回リトライで再取得できるように)
  p.catch(() => tileCache.delete(key));
  return p;
}

/**
 * bbox (+padM) を覆う z16 タイルの road feature を全て返す。
 * **失敗 (トークン未設定・1枚でも取得不能・タイル数超過) は null**、道なしは []。
 */
export async function fetchRoadFeaturesForBbox(
  bbox: [number, number, number, number],
  padM = 0,
): Promise<TileRoadFeature[] | null> {
  if (!MAPBOX_TOKEN) return null;
  const padded = padM > 0 ? padBbox(bbox, padM) : bbox;
  const tiles = tilesCoveringBbox(padded, ROAD_TILE_ZOOM);
  if (tiles.length === 0 || tiles.length > MAX_TILES_PER_FETCH) return null;
  try {
    const results = await Promise.all(
      tiles.map((t) => tileFeatures(ROAD_TILE_ZOOM, t.x, t.y)),
    );
    return results.flat();
  } catch {
    return null;
  }
}

/** 点群の bbox。 */
export function bboxOfPoints(
  points: Array<{ lng: number; lat: number }>,
): [number, number, number, number] | null {
  if (points.length === 0) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const p of points) {
    if (p.lng < w) w = p.lng;
    if (p.lng > e) e = p.lng;
    if (p.lat < s) s = p.lat;
    if (p.lat > n) n = p.lat;
  }
  return [w, s, e, n];
}
